<template>
    <canvas ref="canvas" height="160" style="width: 100%;" />
</template>

<script>
const PAD_TOP  = 52
const PAD_LEFT = 38
const PAD_RIGHT  = 8
const BAR_H      = 22
const ROW_GAP    = 10

const COLOR_SUN   = 'rgba(245, 185, 50, 0.65)'
const COLOR_MOON  = 'rgba(90, 140, 200, 0.65)'
const COLOR_BOTH  = 'rgba(74, 167, 100, 0.75)'
const COLOR_TRACK = 'rgba(0, 0, 0, 0.06)'

function intersect(a1, a2, b1, b2) {
    const lo = Math.max(a1, b1)
    const hi = Math.min(a2, b2)
    return lo < hi ? [lo, hi] : null
}

export default {
    name: 'SolunarChart',
    props: {
        solunarData: { type: Object,  default: null },
        sunriseMin:  { type: Number,  default: null },
        sunsetMin:   { type: Number,  default: null },
        title:       { type: String,  default: '' },
        subtitle:    { type: String,  default: '' },
        subtitle2:   { type: String,  default: '' },
        pad:         { type: Number,  default: 30 },
    },
    watch: {
        solunarData() { this.redraw() },
        sunriseMin()  { this.redraw() },
        sunsetMin()   { this.redraw() },
        pad()         { this.redraw() },
    },
    mounted() {
        this.redraw()
    },
    methods: {
        redraw() {
            const canvas = this.$refs.canvas
            if (!canvas || !this.solunarData) return

            const dpr  = window.devicePixelRatio || 1
            const cssW = canvas.parentElement ? canvas.parentElement.clientWidth : 600
            const cssH = parseInt(canvas.style.height) || canvas.height
            canvas.width  = cssW * dpr
            canvas.height = cssH * dpr
            canvas.style.height = cssH + 'px'
            const ctx = canvas.getContext('2d')
            ctx.scale(dpr, dpr)
            const w = cssW
            const h = cssH

            const plotW = w - PAD_LEFT - PAD_RIGHT
            const toX = min => PAD_LEFT + (min / 1440) * plotW

            ctx.clearRect(0, 0, w, h)

            // Title + subtitle
            if (this.title) {
                ctx.font = 'bold 11px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.6)'
                ctx.fillText(this.title, PAD_LEFT, 14)
            }
            if (this.subtitle) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle, PAD_LEFT, 26)
            }
            if (this.subtitle2) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle2, PAD_LEFT, 38)
            }

            // Extract moonrise / moonset from periods
            const periods = this.solunarData.periods || []
            const moonriseEvent = periods.find(p => p.label === 'Moonrise')
            const moonsetEvent  = periods.find(p => p.label === 'Moonset')
            const moonriseMin   = moonriseEvent ? moonriseEvent.minute_of_day : null
            const moonsetMin    = moonsetEvent  ? moonsetEvent.minute_of_day  : null

            // Build windows (each is [start, end] in minutes, or null)
            const PAD = this.pad
            const srWin = this.sunriseMin !== null ? [this.sunriseMin - PAD, this.sunriseMin + PAD] : null
            const ssWin = this.sunsetMin  !== null ? [this.sunsetMin  - PAD, this.sunsetMin  + PAD] : null
            const mrWin = moonriseMin     !== null ? [moonriseMin     - PAD, moonriseMin     + PAD] : null
            const msWin = moonsetMin      !== null ? [moonsetMin      - PAD, moonsetMin      + PAD] : null

            const sunWindows  = [srWin, ssWin].filter(Boolean)
            const moonWindows = [mrWin, msWin].filter(Boolean)

            // Compute overlaps between every sun window and every moon window
            const bothWindows = []
            for (const sw of sunWindows) {
                for (const mw of moonWindows) {
                    const seg = intersect(sw[0], sw[1], mw[0], mw[1])
                    if (seg) bothWindows.push(seg)
                }
            }

            // Draw one row: track + coloured windows + left label
            const drawRow = (rowIndex, label, windows, color) => {
                const rowTop = PAD_TOP + rowIndex * (BAR_H + ROW_GAP)

                // Track (full-width gray background)
                ctx.fillStyle = COLOR_TRACK
                ctx.fillRect(PAD_LEFT, rowTop, plotW, BAR_H)

                // Coloured windows
                ctx.fillStyle = color
                for (const [a, b] of windows) {
                    const x1 = toX(Math.max(0, a))
                    const x2 = toX(Math.min(1440, b))
                    if (x2 > x1) ctx.fillRect(x1, rowTop, x2 - x1, BAR_H)
                }

                // Track border
                ctx.strokeStyle = 'rgba(0,0,0,0.12)'
                ctx.lineWidth = 1
                ctx.strokeRect(PAD_LEFT, rowTop, plotW, BAR_H)

                // Row label
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'right'
                ctx.fillStyle = 'rgba(0,0,0,0.45)'
                ctx.fillText(label, PAD_LEFT - 5, rowTop + BAR_H / 2 + 4)
            }

            drawRow(0, 'Sun',  sunWindows,  COLOR_SUN)
            drawRow(1, 'Moon', moonWindows, COLOR_MOON)
            drawRow(2, 'Both', bothWindows, COLOR_BOTH)

            // X-axis labels below the bars — every 6 hours
            const barBottom = PAD_TOP + 2 * (BAR_H + ROW_GAP) + BAR_H
            const AXIS_LABELS = ['12am', '6am', '12pm', '6pm', '12am']
            ctx.font = '10px system-ui, sans-serif'
            ctx.fillStyle = 'rgba(0,0,0,0.4)'
            AXIS_LABELS.forEach((label, i) => {
                const x = toX(i * 360)
                ctx.textAlign = i === 0 ? 'left' : i === 4 ? 'right' : 'center'
                ctx.fillText(label, x, barBottom + 14)
            })

        },
    },
}
</script>
